/* eslint-disable camelcase */
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
// @mui
import { Stack, Button, Popover, MenuItem, Typography, Grid, Box, alpha } from '@mui/material';
// components
import { useNavigate } from 'react-router-dom';
import GavelIcon from '@mui/icons-material/Gavel';
import AssignmentAddIcon from '@mui/icons-material/Assignment';
import CreateIcon from '@mui/icons-material/Create';
import { toast } from '../components/toast';
import { getUserGroups } from '../utils';
import { getContracts } from '../services/api';
import Iconify from '../components/iconify';
import { LightTooltip } from '../components/custom-components/custom-tooltip';
import { ContractTable, ContractFilters } from '../components/contracts';
import StatsCards from '../components/common/statsCard';
// utils
import { TABLE_HEAD, DEFAULT_ROWS_PER_PAGE } from '../utils/contractConstants';
import { applySortFilter, getComparator, calculateContractStats } from '../utils/contractTableUtils';

// ----------------------------------------------------------------------

export default function ContractsPage() {
  const [pageLoader, setPageLoader] = useState(false);
  const [contracts, setContracts] = useState([]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const filteredContracts = applySortFilter(contracts, getComparator(order, orderBy), filterName);

  const [anchorEl, setAnchorEl] = useState({});

  const [selectedContractId, setSelectedContractId] = useState('');
  const [selectedAction, setSelectedAction] = useState('');

  const handleActionClick = (event, menuId) => {
    setAnchorEl({ [menuId]: event.currentTarget });
  };

  const handleClose = () => {
    setAnchorEl({});
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = contracts.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleCreateNewContract = () => {
    navigate('new');
  };

  const handleShowContract = (contractId) => {
    setSelectedAction('showContract');
    setSelectedContractId(contractId);
  };

  useEffect(() => {
    if (selectedAction !== '') {
      switch (selectedAction) {
        case 'showContract':
          console.log("Gettin' ready to show contract", selectedContractId);
          navigate(`/contracts/${selectedContractId}`);
          break;
        default:
          break;
      }
    }
  }, [selectedAction, selectedContractId, navigate]);

  const contractStats = calculateContractStats(contracts);

  const statsCards = [
    {
      title: 'Total Agreements',
      value: contractStats.totalAgreements || 10,
      icon: <GavelIcon color="primary" />,
    },
    {
      title: 'Created Agreements',
      value: contractStats.createdAgreements || 3,
      icon: <AssignmentAddIcon color="primary" />,
    },
    {
      title: 'Signed Agreements',
      value: contractStats.signedAgreements || 7,
      icon: <CreateIcon color="primary" />,
    },
  ];

  useEffect(() => {
    setPageLoader(true);
    const userGroups = getUserGroups();
    if (!userGroups.includes('admin') && !userGroups.includes('associate')) {
      navigate('/dashboard', { replace: true });
    } else {
      getContracts()
        .then((contracts) => {
          setContracts(contracts.data.contracts);
        })
        .catch(() => {
          const message = 'Cannot get contracts, try again later.';
          console.log(message);
          toast.error(message);
        })
        .finally(() => {
          setPageLoader(false);
        });
    }
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>BillerBay Transmit | Agreements</title>
      </Helmet>

      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={12}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                Agreement Management
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Oversee and handle all business associate agreements and service contracts.
              </Typography>
            </Box>
            <LightTooltip title="Initiate a new business associate agreement.">
              <Button
                variant="contained"
                onClick={handleCreateNewContract}
                startIcon={<Iconify icon="eva:plus-fill" />}
                sx={{
                  backgroundColor: '#0A9EF2',
                  boxShadow: () => alpha('#0A9EF2', 0.24),
                  '&:hover': {
                    backgroundColor: '#0886cc',
                    boxShadow: 'none',
                  },
                  px: 2.5,
                  py: 1,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                New Agreement
              </Button>
            </LightTooltip>
          </Stack>

          <StatsCards stats={statsCards} />

          <ContractFilters numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />
          <ContractTable
            contracts={filteredContracts}
            isLoading={pageLoader}
            page={page}
            rowsPerPage={rowsPerPage}
            order={order}
            orderBy={orderBy}
            selected={selected}
            anchorEl={anchorEl}
            onRequestSort={handleRequestSort}
            onSelectAllClick={handleSelectAllClick}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onActionClick={handleActionClick}
            onClose={handleClose}
            onShowContract={handleShowContract}
            TABLE_HEAD={TABLE_HEAD}
          />

          <Popover
            open={Boolean(open)}
            anchorEl={open}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                p: 1,
                width: 140,
                '& .MuiMenuItem-root': {
                  px: 1,
                  typography: 'body2',
                  borderRadius: 0.75,
                },
              },
            }}
          >
            <MenuItem onClick={handleCreateNewContract} disabled>
              <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
              Edit
            </MenuItem>

            <MenuItem sx={{ color: 'error.main' }} disabled>
              <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
              Delete
            </MenuItem>
          </Popover>
        </Grid>
      </Grid>
    </>
  );
}
